/**
 * Where the signed-in admin's tokens live.
 *
 * localStorage keeps the session across reloads and tabs, which is what you
 * want for a tool people leave open all day. The trade-off is that any script
 * running on this origin could read the token, so the app loads no third-party
 * scripts beyond the Google font stylesheet.
 */
import type { StaffRole } from './types'

const KEY = 'fulfil.session.v1'

export interface Session {
  accessToken: string
  refreshToken: string
  staffId: string
  username: string
  firstName: string
  lastName: string
  role: StaffRole
  mustChangePassword: boolean
}

let cached: Session | null | undefined

export function getSession(): Session | null {
  if (cached !== undefined) return cached
  try {
    const raw = localStorage.getItem(KEY)
    cached = raw ? (JSON.parse(raw) as Session) : null
  } catch {
    // Private-mode or corrupted storage: behave as signed out.
    cached = null
  }
  return cached
}

export function setSession(session: Session | null): void {
  cached = session
  try {
    if (session) localStorage.setItem(KEY, JSON.stringify(session))
    else localStorage.removeItem(KEY)
  } catch {
    // Nothing useful to do — the in-memory copy still carries this tab.
  }
}

/** Replaces just the tokens, after a refresh, keeping the identity fields. */
export function updateTokens(accessToken: string, refreshToken: string): void {
  const current = getSession()
  if (!current) return
  setSession({ ...current, accessToken, refreshToken })
}

/** True for the root `admin` account, which alone may see usage and margins. */
export function isRootAdmin(session: Session | null): boolean {
  return session?.username === 'admin'
}
